import { useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAdminContent, AdminTitle } from "./shared";
import ImageField from "./ImageField";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", image: "", status: "published" };

export default function BlogManager() {
  const { content, reload } = useAdminContent();
  const [draft, setDraft] = useState(null);

  if (!content) return <p className="text-clay">Loading…</p>;
  const posts = content.blog.map((d) => d.staging || d.live).filter(Boolean).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  const save = async () => {
    try {
      await api.post("/admin/blog", draft);
      toast.success("Post saved to staging");
      setDraft(null);
      reload();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Post deleted");
    reload();
  };

  return (
    <div data-testid="blog-manager">
      <AdminTitle eyebrow="The Journal" title="Blog Posts">
        <button onClick={() => setDraft({ ...EMPTY })} data-testid="blog-add-button" className="flex items-center gap-2 rounded-full bg-brass px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-cast hover:bg-flame hover:text-cream transition-colors">
          <Plus size={14} /> New Post
        </button>
      </AdminTitle>
      <div className="grid gap-8 lg:grid-cols-[1fr_420px]">
        <div className="rounded-sm border border-brass/10 bg-card divide-y divide-border">
          {posts.map((post) => (
            <div key={post.id} className="flex items-center gap-4 px-5 py-4" data-testid={`blog-row-${post.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-cream truncate">{post.title}</p>
                <p className="text-xs text-clay">/journal/{post.slug} · <span className={post.status === "published" ? "text-brass" : "text-flame"}>{post.status}</span></p>
              </div>
              <button onClick={() => setDraft({ ...post })} data-testid={`blog-edit-${post.id}`} className="text-xs text-brass hover:underline">Edit</button>
              <button onClick={() => remove(post.id)} data-testid={`blog-delete-${post.id}`} className="text-flame"><Trash2 size={14} /></button>
            </div>
          ))}
          {!posts.length && <p className="px-5 py-8 text-sm text-clay">No posts yet.</p>}
        </div>
        <div>
          {draft ? (
            <div className="rounded-sm border border-brass/10 bg-card p-6 space-y-4 sticky top-24" data-testid="blog-edit-form">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass">{draft.id ? "Edit Post" : "New Post"}</p>
              <div><label className="admin-label">Title</label><input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="admin-input" data-testid="blog-title-input" /></div>
              <div><label className="admin-label">Slug (blank = auto)</label><input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} className="admin-input" data-testid="blog-slug-input" /></div>
              <div><label className="admin-label">Excerpt</label><textarea rows={2} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} className="admin-input" data-testid="blog-excerpt-input" /></div>
              <div><label className="admin-label">Content (blank line between paragraphs)</label><textarea rows={10} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} className="admin-input" data-testid="blog-content-input" /></div>
              <div><label className="admin-label">Cover Image</label><ImageField value={draft.image} onChange={(v) => setDraft({ ...draft, image: v })} testid="blog-image" /></div>
              <div>
                <label className="admin-label">Status</label>
                <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="admin-input" data-testid="blog-status-select">
                  <option value="published">Published</option>
                  <option value="draft">Draft (hidden on live)</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={save} disabled={!draft.title} data-testid="blog-save-button" className="flex items-center gap-2 rounded-full bg-brass px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-cast hover:bg-flame hover:text-cream transition-colors disabled:opacity-40">
                  <Save size={14} /> Save
                </button>
                <button onClick={() => setDraft(null)} data-testid="blog-cancel-button" className="text-xs text-clay hover:text-cream">Cancel</button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-clay" data-testid="blog-edit-empty">Select a post to edit, or start a new one.</p>
          )}
        </div>
      </div>
    </div>
  );
}
